import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { 
  AdminAuthProvider, 
  useAdminAuth,
  AdminLayout,
  AdminLoginPage,
  AdminDashboardPage,
  AdminUsersPage,
  AdminAuditLogPage,
  AdminAIRiskPage,
  Setup2FAPage,
  ChangePasswordPage
} from '@/admin';

// Protected route wrapper for admin
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, requiresPasswordReset, requires2FASetup } = useAdminAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/secure-admin/login" replace />;
  }

  // Force password reset flow
  if (requiresPasswordReset) {
    return <ChangePasswordPage />;
  }

  // Force 2FA setup flow
  if (requires2FASetup) {
    return <Setup2FAPage />;
  }

  return children;
};

// Admin routes component
const AdminRoutes = () => {
  const { isAuthenticated } = useAdminAuth();

  return (
    <Routes>
      {/* Public admin routes */}
      <Route 
        path="/login" 
        element={
          isAuthenticated ? <Navigate to="/secure-admin/dashboard" replace /> : <AdminLoginPage />
        } 
      />

      {/* Protected admin routes */}
      <Route
        path="/dashboard"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <AdminDashboardPage />
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <AdminUsersPage />
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/listings"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <div className="p-6 text-white">Listings management coming soon...</div>
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/payments"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <div className="p-6 text-white">Payments management coming soon...</div>
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/disputes"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <div className="p-6 text-white">Disputes management coming soon...</div>
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/audit-log"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <AdminAuditLogPage />
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/ai-risk"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <AdminAIRiskPage />
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      <Route
        path="/config"
        element={
          <AdminProtectedRoute>
            <AdminLayout>
              <div className="p-6 text-white">Configuration coming soon...</div>
            </AdminLayout>
          </AdminProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/secure-admin/login" replace />} />
      <Route path="*" element={<Navigate to="/secure-admin/dashboard" replace />} />
    </Routes>
  );
};

// Main Admin App with provider
export const AdminApp = () => {
  return (
    <AdminAuthProvider>
      <AdminRoutes />
    </AdminAuthProvider>
  );
};

export default AdminApp;
